// src/components/workspace/FilterChips.tsx
// What is currently narrowing the list, as removable chips.
//
// The editors moved into a menu; this did not. A filter you cannot see is a
// list that is quietly missing rows — "where did my data go" — so the state
// stays in sight even though the controls that set it no longer are. When
// nothing is filtered there are no chips at all, which is the point: a clean
// list starts at its first row.
//
// One line, never two: these sit in the navbar beside the settings button, and
// a row that grows a second line moves everything under it. Past the width of
// that row the chips scroll.

import { observer } from '@react';
import { useI18n } from '@react/hooks';

import { useWorkspace } from '@/features/workspace/context';
import { useUserNames } from '@/features/workspace/useUserNames';

import type { FilterValue } from '@/features/workspace/types';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

const chipClass =
  'inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground';

export const FilterChips = observer(() => {
  const { t } = useI18n();
  const { state, config, dispatch, searchDraft, onSearchDraftChange } = useWorkspace();

  // People filters (assignee, creator) hold profile ids; resolve them to names.
  const userIds = state.filters.flatMap((filter) => {
    const cfg = config.availableFilters.find((f) => f.field === filter.field);
    if (cfg?.type !== 'user') return [];
    return Array.isArray(filter.value) ? (filter.value as string[]) : [String(filter.value)];
  });
  const userNames = useUserNames(userIds);

  const hasSearch = searchDraft.trim().length > 0;
  if (state.filters.length === 0 && !hasSearch) return null;

  const displayValue = (filter: FilterValue): string => {
    const cfg = config.availableFilters.find((f) => f.field === filter.field);
    if (filter.operator === 'between' && Array.isArray(filter.value)) {
      const [from, to] = filter.value as [string, string];
      if (from && to) return `${from} → ${to}`;
      return from ? `≥ ${from}` : `≤ ${to}`;
    }
    if (Array.isArray(filter.value)) {
      const values = filter.value as string[];
      if (values.length <= 2) {
        return values
          .map((v) => {
            if (cfg?.type === 'user') {
              return userNames[v] || `${v.slice(0, 8)}…`;
            }
            const option = cfg?.options?.find((o) => o.value === v);
            return option ? t(option.labelKey) : v;
          })
          .join(', ');
      }
      return t('workspace.filter.countValues', { count: values.length });
    }
    return String(filter.value);
  };

  return (
    <div className="no-scrollbar flex min-w-0 items-center gap-1.5 overflow-x-auto">
      {hasSearch && (
        <span className={chipClass}>
          <SearchIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
          <span className="max-w-[160px] truncate">{searchDraft}</span>
          <button
            type="button"
            onClick={() => onSearchDraftChange('')}
            aria-label={t('workspace.search.clear')}
            className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover:bg-accent/50 hover:text-foreground"
          >
            <XIcon className="h-3 w-3" />
          </button>
        </span>
      )}

      {state.filters.map((filter) => {
        const cfg = config.availableFilters.find((f) => f.field === filter.field);
        const label = cfg ? t(cfg.labelKey) : filter.field;
        return (
          <span key={filter.field} className={chipClass}>
            <FilterIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">{label}:</span>
            <span className="max-w-[140px] truncate">{displayValue(filter)}</span>
            <button
              type="button"
              onClick={() => dispatch({ type: 'REMOVE_FILTER', field: filter.field })}
              aria-label={t('workspace.filter.remove', { field: label })}
              className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover:bg-accent/50 hover:text-foreground"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </span>
        );
      })}

      <button
        type="button"
        onClick={() => {
          onSearchDraftChange('');
          dispatch({ type: 'CLEAR_FILTERS' });
        }}
        className="flex-shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('workspace.filter.clearAll')}
      </button>
    </div>
  );
});

FilterChips.displayName = 'FilterChips';
